import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import { useSession } from '../auth/queries';
import { roleLabel, useRoles } from '../roles/queries';
import {
  nameOf,
  useAssignableUsers,
  useCreateAgent,
  useResetAgentPassword,
  useResetMfa,
  useUpdateAgent,
} from './queries';
import { Select } from '../../components/Select';

/**
 * Die Zugaenge des Mandanten -- F5-16.
 *
 * **Der erste Zugang kommt vom Betreiber, jeder weitere von hier.** Ein
 * Mandant entsteht mit genau einem Administrator ueber
 * ``scripts/provision_tenant.py`` -- er hat sonst niemanden, der ihn anlegen
 * koennte. Fuer den vierten Mitarbeiter waere derselbe Weg keine
 * Sicherheitsmassnahme mehr, sondern ein Anruf beim Dienstleister.
 *
 * Alles hier verlangt ``users.write`` und nicht ``masterdata.write``: Wer
 * Zugaenge vergibt, vergibt Rechte, und wer Kennwoerter zuruecksetzt, kommt
 * an fremde Konten. Das gehoert getrennt nachweisbar (Migration 0036).
 *
 * **Abschalten statt loeschen.** Ein Mitarbeiter, der geht, wird stillgelegt:
 * Seine Tickets, Beitraege und Zeiteintraege tragen seine Kennung, und ein
 * geloeschter Nutzer machte aus jedem Protokolleintrag eine leere Stelle.
 *
 * **Der Rueckweg bei verlorenem Telefon.** Es gibt keine
 * Wiederherstellungscodes; das ist eine ausdrueckliche Entscheidung (docs/04,
 * Abschnitt 4): Ein Code auf einem Zettel ist ein zweiter Faktor, der nicht
 * am Telefon haengt, und damit genau das, was der zweite Faktor verhindern
 * soll. Der Rueckweg ist stattdessen dieser Knopf -- ein anderer
 * Administrator setzt zurueck, und der Vorgang steht im Protokoll.
 *
 * Ohne ihn waere die Entscheidung gegen die Codes keine Entscheidung
 * gewesen, sondern eine Sackgasse: Wer sein Telefon verliert, kaeme an kein
 * einziges Recht mehr heran.
 */
export function UsersPage() {
  const { t, i18n } = useTranslation();
  const users = useAssignableUsers();
  const session = useSession();
  const reset = useResetMfa();
  const create = useCreateAgent();
  const update = useUpdateAgent();
  const resetPassword = useResetAgentPassword();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  // Leer heisst: die Vorgabe des Servers, "Bearbeitung".
  const [roleId, setRoleId] = useState<string>('');
  const roles = useRoles();

  const canReset = (session.data?.permissions ?? []).includes('users.write');
  const everyone = users.data ?? [];
  const ownPermissions = new Set(session.data?.permissions ?? []);
  const darfVergeben = (permissions: string[]) =>
    permissions.every((permission) => ownPermissions.has(permission));
  // Vergebbar sind die Mitarbeiterrollen -- mitgelieferte und eigene --, und
  // davon die, die nicht mehr duerfen als man selbst. Den Rest weist der
  // Server ab; ihn gar nicht erst anzubieten erspart den Fehler. Die
  // Portalrollen entstehen am Ansprechpartner im CRM.
  const vergebbar = (roles.data ?? []).filter(
    (item) => item.assignable && darfVergeben(item.permissions),
  );
  const rolleVon = (id: string | null | undefined) =>
    (roles.data ?? []).find((item) => item.id === id) ?? null;

  // Das Erstkennwort steht **einmal** in der Antwort und danach nirgends
  // mehr. Es bleibt hier stehen, bis der naechste Vorgang es ersetzt -- wer
  // es wegklickt, bevor er es weitergegeben hat, laesst es neu vergeben.
  const geheimnis = create.data?.initial_password ?? resetPassword.data?.initial_password ?? null;
  const geheimnisFuer =
    create.data?.user.email ?? everyone.find((user) => user.id === resetPassword.variables)?.email;

  return (
    <>
      <PageHeader title={t('users.title')} subtitle={t('users.subtitle')} />

      {users.isPending ? <SkeletonTable columns={5} /> : null}
      {users.isError ? <ErrorNotice error={users.error} /> : null}
      {reset.isError ? <ErrorNotice error={reset.error} /> : null}

      {reset.isSuccess ? (
        <p className="field__hint" role="status">
          {t('users.mfaResetDone')}
        </p>
      ) : null}

      {create.isError ? <ErrorNotice error={create.error} /> : null}
      {update.isError ? <ErrorNotice error={update.error} /> : null}
      {resetPassword.isError ? <ErrorNotice error={resetPassword.error} /> : null}

      {/* Das Erstkennwort. Es steht hier einmal -- abgelegt ist nur der
          Abdruck, ein Nachschlagen gibt es nicht. */}
      {geheimnis ? (
        <section className="card notice--vip" role="status">
          <h2>{t('users.initialPassword')}</h2>
          <p className="table__muted">{t('users.initialPasswordFor', { email: geheimnisFuer })}</p>
          <p className="kpi__value" style={{ fontFamily: 'var(--font-mono)', userSelect: 'all' }}>
            {geheimnis}
          </p>
          <p className="field__hint">{t('users.initialPasswordHint')}</p>
        </section>
      ) : null}

      {canReset ? (
        <section className="card">
          <h2>{t('users.newAgent')}</h2>
          <p className="table__muted">{t('users.newAgentHint')}</p>

          <div className="row">
            <TextField
              label={t('crm.contactName')}
              value={fullName}
              onChange={(event) => {
                setFullName(event.target.value);
              }}
            />
            <TextField
              label={t('auth.email')}
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
            <div className="field">
              <label className="field__label" htmlFor="agent-role">
                {t('users.role')}
              </label>
              <Select
                id="agent-role"
                value={roleId || (vergebbar.find((item) => item.key === 'agent')?.id ?? '')}
                onChange={(event) => {
                  setRoleId(event.target.value);
                }}
              >
                {vergebbar.map((item) => (
                  <option key={item.id} value={item.id}>
                    {roleLabel(item, t)}
                  </option>
                ))}
              </Select>
              <span className="field__hint">
                {t('users.roleHint')}{' '}
                <Link to="/app/administration/roles">{t('users.rolesLink')}</Link>
              </span>
            </div>
          </div>

          <Button
            variant="primary"
            disabled={!fullName.trim() || !email.trim()}
            busy={create.isPending}
            onClick={() => {
              create.mutate(
                {
                  email: email.trim(),
                  full_name: fullName.trim(),
                  ...(roleId ? { role_id: roleId } : {}),
                },
                {
                  onSuccess: () => {
                    setEmail('');
                    setFullName('');
                  },
                },
              );
            }}
          >
            {t('masterdata.add')}
          </Button>
        </section>
      ) : null}

      <section className="card card--flush">
        {everyone.length === 0 ? (
          <EmptyState icon="users" title={t('users.empty')} hint={t('users.emptyHint')} />
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('crm.contactName')}</th>
                  <th scope="col">{t('auth.email')}</th>
                  <th scope="col">{t('users.role')}</th>
                  <th scope="col">{t('masterdata.isActive')}</th>
                  <th scope="col">{t('absence.title')}</th>
                  {canReset ? <th scope="col">{t('tickets.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {everyone.map((user) => (
                  <tr key={user.id}>
                    <td>{user.full_name}</td>
                    <td className="table__muted">{user.email}</td>
                    <td>
                      {/* Wechseln laesst sich die Rolle anderer, nicht die
                          eigene, nicht die eines Zugangs, der mehr darf als
                          man selbst, und nicht die eines Portalzugangs --
                          alles das weist der Server ab, und die Auswahl
                          fehlt deshalb gleich. Steht die heutige Rolle in
                          der vergebbaren Liste, trifft keines davon zu. */}
                      {canReset &&
                      user.id !== session.data?.user_id &&
                      vergebbar.some((item) => item.id === user.role_id) ? (
                        <Select
                          aria-label={t('users.changeRole')}
                          value={user.role_id ?? ''}
                          disabled={update.isPending}
                          onChange={(event) => {
                            update.mutate({
                              userId: user.id,
                              payload: { role_id: event.target.value },
                            });
                          }}
                        >
                          {vergebbar.map((item) => (
                            <option key={item.id} value={item.id}>
                              {roleLabel(item, t)}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        (() => {
                          const rolle = rolleVon(user.role_id);
                          return rolle ? roleLabel(rolle, t) : '—';
                        })()
                      )}
                    </td>
                    <td>
                      {user.is_active ? (
                        <Badge tone="success">{t('masterdata.yes')}</Badge>
                      ) : (
                        <Badge tone="neutral">{t('masterdata.inactive')}</Badge>
                      )}
                    </td>
                    <td className="table__muted">
                      {!user.out_of_office_until ? (
                        '—'
                      ) : (
                        <>
                          {formatDateTime(user.out_of_office_until, i18n.language)}
                          {user.substitute_user_id ? (
                            <div>
                              {t('absence.substitute')}:{' '}
                              {nameOf(users.data, user.substitute_user_id)}
                            </div>
                          ) : null}
                        </>
                      )}
                    </td>
                    {canReset ? (
                      <td>
                        <Button
                          variant="ghost"
                          busy={resetPassword.isPending}
                          onClick={() => {
                            if (
                              !window.confirm(
                                t('users.resetPasswordConfirm', { name: user.full_name }),
                              )
                            )
                              return;
                            resetPassword.mutate(user.id);
                          }}
                        >
                          {t('users.resetPassword')}
                        </Button>
                        <Button
                          variant="ghost"
                          busy={reset.isPending}
                          onClick={() => {
                            if (
                              !window.confirm(t('users.mfaResetConfirm', { name: user.full_name }))
                            )
                              return;
                            reset.mutate(user.id);
                          }}
                        >
                          {t('users.resetMfa')}
                        </Button>
                        {/* Der eigene Zugang fehlt hier: Wer sich selbst
                            abschaltet, saesse vor einem Mandanten, in dem es
                            vielleicht keinen zweiten Administrator gibt. Der
                            Server weist es ohnehin ab -- das Ausblenden
                            erspart den Fehler, den man vorher sah. */}
                        {user.id === session.data?.user_id ? null : (
                          <Button
                            variant="ghost"
                            busy={update.isPending}
                            onClick={() => {
                              update.mutate({
                                userId: user.id,
                                payload: { is_active: !user.is_active },
                              });
                            }}
                          >
                            {user.is_active ? t('users.deactivate') : t('users.activate')}
                          </Button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
